import {
  Box,
  Button,
  createListCollection,
  Dialog,
  Icon,
  Input,
  Menu,
  Portal,
  Select,
  Stack,
  Text,
  useDisclosure,
  useSlotRecipe,
} from '@chakra-ui/react';
import { toaster } from './ui/toaster';
import { useAppDispatch, useAppSelector } from '../lib/hooks';
import { CSSProperties, useEffect, useState, useMemo } from 'react';
import { setDocumentStructure } from '../lib/state/slices/ProjectStateSlice';
import { Folder } from '../lib/state/ViewTypes';
import { Edit2, MoreVertical, PlusCircle, Trash2 } from 'react-feather';
import { v4 as uuidv4 } from 'uuid';
import DeleteFolderDialog from './Dialogs/DeleteFolderDialog';
import { selectRecipe } from '@/theme';
import StyledButton from './ui/styledButton';

export default function FolderSelector(props: {
  style?: CSSProperties;
  onFolderOpen: (folderId: string) => void;
}) {
  const folders = useAppSelector((state) => state.viewReducer.documents.structure.FOLDER);
  const folderValues = Object.values(folders);

  const recipe = useSlotRecipe({ recipe: selectRecipe });
  const style = recipe();

  const dispatch = useAppDispatch();
  const [folderName, setFolderName] = useState<string>('');
  const [openFolderID, setOpenFolderID] = useState<string>(
    folderValues.length > 0 ? folderValues[0].id : '',
  );

  const {
    open: isCreateDialogOpen,
    onOpen: onCreateDialogOpen,
    onClose: onCreateDialogClose,
  } = useDisclosure();
  const {
    open: isRenameDialogOpen,
    onOpen: onRenameDialogOpen,
    onClose: onRenameDialogClose,
  } = useDisclosure();
  const {
    open: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();

  useEffect(() => {
    props.onFolderOpen(openFolderID);
  }, [openFolderID]);

  const folderSelection = useMemo(() => {
    return createListCollection({
      items: folderValues.map((folder: Folder) => {
        return { label: folder.name, value: folder.id };
      }),
    });
  }, [folders]);

  useEffect(() => {
    if (folderValues.length === 0) {
      setOpenFolderID('');
    } else if (!folderValues.find((folder) => folder.id === openFolderID)) {
      setOpenFolderID(folderValues[0].id);
    }
  }, [folders]);

  return (
    <Box
      style={{
        ...props.style,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Text color="secondaryText" fontWeight={700} fontSize={'medium'} marginRight={2}>
        Select folder
      </Text>
      {folderValues.length > 0 ? (
        <Select.Root
          css={style.root}
          size="sm"
          borderRadius="0.375rem"
          background="cardDefault"
          _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
          _focus={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
          gap={0}
          width={'80%'}
          onValueChange={(details: { value: string[] }) => {
            setOpenFolderID(details.value[0]);
            props.onFolderOpen(details.value[0]);
          }}
          collection={folderSelection}
          defaultValue={[openFolderID]}
          value={[openFolderID]}
        >
          <Select.HiddenSelect />
          <Select.Label />
          <Select.Control css={style.control}>
            <Select.Trigger width={'100%'} css={style.trigger}>
              <Select.ValueText width={'100%'} color="secondaryText" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator color="secondaryText" />
            </Select.IndicatorGroup>
          </Select.Control>

          <Portal>
            <Select.Positioner>
              <Select.Content backgroundColor="cardDefault" color="secondaryText">
                {folderValues
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((folder: Folder) => (
                    <Select.Item
                      color="secondaryText"
                      css={{
                        '&[data-state="checked"]': {
                          background: 'cardActive',
                        },
                      }}
                      background="cardDefault"
                      _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                      _focus={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
                      key={folder.id}
                      item={{ label: folder.name, value: folder.id }}
                    >
                      {folder.name}
                    </Select.Item>
                  ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      ) : (
        <Text>None</Text>
      )}

      <Menu.Root>
        <Menu.Trigger>
          <Button
            backgroundColor="cardDefault"
            _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
            _focus={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
            size={'sm'}
            variant={'subtle'}
            as={'div'}
          >
            <Icon color="secondaryText">
              <MoreVertical />
            </Icon>
          </Button>
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content backgroundColor="cardDefault">
            <Menu.Item
              css={{
                '&[data-state="checked"]': {
                  background: 'cardActive',
                },
              }}
              color="secondaryText"
              _focus={{ bg: 'cardDefault' }}
              _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
              _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
              value="addFolder"
              onClick={() => {
                setFolderName('');
                onCreateDialogOpen();
              }}
            >
              <PlusCircle />
              <Box flex={1}>Create Folder</Box>
            </Menu.Item>
            <Menu.Item
              css={{
                '&[data-state="checked"]': {
                  background: 'cardActive',
                },
              }}
              color="secondaryText"
              _focus={{ bg: 'cardDefault' }}
              _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
              _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
              value="renameFolder"
              onClick={() => {
                setFolderName(
                  folderValues.find((folder) => folder.id === openFolderID)?.name || '',
                );
                onRenameDialogOpen();
              }}
            >
              <Edit2 />
              <Box flex={1}>Rename Folder</Box>
            </Menu.Item>
            <Menu.Item
              css={{
                '&[data-state="checked"]': {
                  background: 'cardActive',
                },
              }}
              color="accentRed"
              _focus={{ bg: 'cardDefault' }}
              _hover={{ bg: 'cardHover', color: 'accentRed', transition: 'background-color 0.2s' }}
              _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
              value="deleteFolder"
              onClick={() => {
                onDeleteDialogOpen();
              }}
            >
              <Trash2 />
              <Box flex={1}>Delete Folder</Box>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>

      <Dialog.Root lazyMount open={isCreateDialogOpen} onOpenChange={onCreateDialogClose}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content backgroundColor="midCardBG">
              <Dialog.Header>
                <Dialog.Title color="primaryText">Create Folder</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Stack>
                  <Input
                    variant="subtle"
                    outline="none"
                    backgroundColor="cardDefault"
                    color="secondaryText"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder="Folder Name"
                  />
                </Stack>
              </Dialog.Body>
              <Dialog.Footer>
                <StyledButton variant="outline" text="Cancel" onClick={onCreateDialogClose} />
                <StyledButton
                  variant="solid"
                  text="Create"
                  onClick={() => {
                    if (folderName.trim().length > 0) {
                      let id = uuidv4();
                      dispatch(
                        setDocumentStructure({
                          type: 'FOLDER',
                          key: id,
                          value: { id: id, name: folderName.trim(), documents: [] },
                        }),
                      );
                      setOpenFolderID(id);
                      onCreateDialogClose();
                    } else {
                      toaster.create({
                        type: 'error',
                        status: 'error',
                        title: 'Error',
                        description: 'Folder name cannot be empty',
                      });
                    }
                  }}
                />
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <Dialog.Root lazyMount open={isRenameDialogOpen} onOpenChange={onRenameDialogClose}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content backgroundColor="midCardBG">
              <Dialog.Header>
                <Dialog.Title color="primaryText">Rename Folder</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Stack>
                  <Input
                    variant="subtle"
                    outline="none"
                    backgroundColor="cardDefault"
                    color="secondaryText"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder="Folder Name"
                  />
                </Stack>
              </Dialog.Body>
              <Dialog.Footer>
                <StyledButton variant="outline" text="Cancel" onClick={onRenameDialogClose} />
                <StyledButton
                  variant="solid"
                  text="Create"
                  onClick={() => {
                    if (folderName.trim().length > 0) {
                      dispatch(
                        setDocumentStructure({
                          type: 'FOLDER',
                          key: openFolderID,
                          value: {
                            id: openFolderID,
                            name: folderName.trim(),
                            documents:
                              folderValues.find((folder) => folder.id === openFolderID)
                                ?.documents || [],
                          },
                        }),
                      );
                      onRenameDialogClose();
                    } else {
                      toaster.create({
                        type: 'error',
                        title: 'Error',
                        description: 'Folder name cannot be empty',
                        status: 'error',
                      });
                    }
                  }}
                />
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <DeleteFolderDialog
        isOpen={isDeleteDialogOpen}
        onClose={onDeleteDialogClose}
        folderId={openFolderID}
      />
    </Box>
  );
}
